import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { showToast } from '../utils/errorHandler';
import { User, Mail, Edit3, Save, X, Briefcase, Check, AlertCircle, Heart, Loader2, Sparkles, Trash2, MapPin, Globe, Target, Star, MessageCircle, AlertTriangle, Eye, RefreshCw, BarChart3, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePaymentsVisibility } from '../hooks/usePaymentsVisibility';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mapLevelValues } from '../utils/levelMapper';
import { getMockQuota, type MockQuota } from '../api/interview';
import PassportPreviewCard from '../components/Passport/PassportPreviewCard';
import { EXPLORE_CREATORS_ENABLED, JOB_RADAR_ENABLED } from '../config/features';

/** Usage rows still offered on Explorer. Paused products stay out of this list. */
const LIVE_EXPLORER_USAGE_FEATURES = ['cv_search', 'reach_outs', 'cv_upload'] as const;

function explorerUsageFeatureKeys(): string[] {
  const keys: string[] = [...LIVE_EXPLORER_USAGE_FEATURES];
  if (EXPLORE_CREATORS_ENABLED) keys.push('creator_search');
  if (JOB_RADAR_ENABLED) keys.push('job_watch');
  return keys;
}

interface ProfileData {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  career_status: string | null;
  cv_id: string | null;
  base_locations: string | null;
  preferred_locations: string | null;
  role_preferences: string | null;
  current_role: string | null;
  current_level: string | null;
  is_active: boolean;
  subscription_type: string;
  subscription_period_end: number | null;  // Unix timestamp - Usage reset date
  next_billing_date: number | null;  // Unix timestamp - Next billing date
  cancel_at_period_end?: boolean;  // Is subscription canceled at period end
  is_recruiter: boolean;
  is_contactable: boolean;
  real_talk_sentence: string | null;
  real_talk_tags: string[] | null;
  // ATS Readability Score
  ats_readability_score: number | null;
  ats_readability_summary: string | null;
  ats_readability_updated_at: number | null;
  ats_readability_cv_id: string | null;
  created_at: number;  // Unix timestamp
  updated_at: number;  // Unix timestamp
}

interface FeatureUsage {
  used: number;
  limit: number;
  remaining: number;
}

interface UsageStats {
  global: FeatureUsage;
  features: {
    cv_search: FeatureUsage;
    creator_search: FeatureUsage;
    company_search: FeatureUsage;
    candidate_search: FeatureUsage;
    candidate_analysis: FeatureUsage;
    vee_chat: FeatureUsage;
    reach_outs: FeatureUsage;
    cv_upload?: FeatureUsage;
    job_watch?: FeatureUsage;
  };
  resets_at: string;
  subscription_type: string;
}

const Profile: React.FC = () => {
  const { user, apiCall, isSignedIn, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['profile', 'inspire', 'passport']);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingContact, setEditingContact] = useState(false);
  const [editingCareer, setEditingCareer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  
  // CV Images state
  const [cvImages, setCvImages] = useState<string[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  
  // ATS Readability Score state
  const [readabilityScore, setReadabilityScore] = useState<{ score: number; summary: string } | null>(null);
  const [loadingReadability, setLoadingReadability] = useState(false);
  const [displayScore, setDisplayScore] = useState(0);
  
  // Load readability score from profile if exists AND matches current CV
  useEffect(() => {
    if (
      profile?.cv_id &&  // Must have a CV
      profile?.ats_readability_score !== null && 
      profile?.ats_readability_score !== undefined &&
      profile?.ats_readability_cv_id === profile?.cv_id  // Only show if same CV
    ) {
      setReadabilityScore({
        score: profile.ats_readability_score,
        summary: profile.ats_readability_summary || ''
      });
      setDisplayScore(profile.ats_readability_score); // Show immediately, no animation for cached
    } else {
      // Reset if no CV or CV changed
      setReadabilityScore(null);
      setDisplayScore(0);
    }
  }, [profile?.ats_readability_score, profile?.ats_readability_cv_id, profile?.cv_id]);
  
  // Track if we just analyzed (for animation)
  const [justAnalyzed, setJustAnalyzed] = useState(false);
  
  // Animated count-up for score (only when freshly calculated)
  useEffect(() => {
    if (readabilityScore && justAnalyzed) {
      setDisplayScore(0);
      let start = 0;
      const end = readabilityScore.score;
      const duration = 3000;
      const stepTime = 30;
      const steps = duration / stepTime;
      const increment = end / steps;
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setDisplayScore(end);
          setJustAnalyzed(false); // Reset after animation
          clearInterval(timer);
        } else {
          setDisplayScore(Math.floor(start));
        }
      }, stepTime);
      
      return () => clearInterval(timer);
    }
  }, [readabilityScore, justAnalyzed]);
  
  // Usage Stats state
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [mockQuota, setMockQuota] = useState<MockQuota | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingMockQuota, setLoadingMockQuota] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    plan: string;
    billing_cycle: string;
    status: string;
    next_billing_date: string;
    amount: string;
    cancel_at_period_end: boolean;
  } | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  
  // Delete Account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Usage & Limits dropdown state
  const [usageExpanded, setUsageExpanded] = useState(false);
  const usageDropdownRef = useRef<HTMLDivElement>(null);
  
  // Click outside to close usage dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (usageDropdownRef.current && !usageDropdownRef.current.contains(event.target as Node)) {
        setUsageExpanded(false);
      }
    };

    if (usageExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [usageExpanded]);
  
  // Job Preferences edit state
  const [editingJobPrefs, setEditingJobPrefs] = useState(false);
  const [levelDropdownOpen, setLevelDropdownOpen] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const levelDropdownRef = useRef<HTMLDivElement>(null);
  
  // Level options for multi-select dropdown (same as CVUploadForm)
  const currentLanguage = i18n.language?.startsWith('de') ? 'de' : 'en';
  const getLevelOptions = () => {
    return [
      t('inspire:levels.highSchool'), t('inspire:levels.apprentice'), t('inspire:levels.bachelor'),
      t('inspire:levels.master'), t('inspire:levels.workingStudent'), t('inspire:levels.phd'),
      t('inspire:levels.qualifiedWorker'), t('inspire:levels.entrepreneur'), t('inspire:levels.junior'),
      t('inspire:levels.midLevel'), t('inspire:levels.senior'), t('inspire:levels.masterCraftsman'),
      t('inspire:levels.lead'), t('inspire:levels.expert')
    ].map(level => ({ value: level, label: level }));
  };

  // Click outside to close level dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(event.target as Node)) {
        setLevelDropdownOpen(false);
      }
    };
    if (levelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [levelDropdownOpen]);

  // Remap selected levels when UI language changes (same as CVUploadForm)
  useEffect(() => {
    if (selectedLevels.length > 0) {
      const remapped = mapLevelValues(selectedLevels, currentLanguage);
      if (JSON.stringify(remapped) !== JSON.stringify(selectedLevels)) {
        setSelectedLevels(remapped);
      }
    }
  }, [currentLanguage]);

  // Sync selectedLevels -> formData.current_level
  useEffect(() => {
    if (editingJobPrefs) {
      setFormData(prev => ({ ...prev, current_level: selectedLevels.join(', ') }));
    }
  }, [selectedLevels, editingJobPrefs]);

  const paymentsEnabled = usePaymentsVisibility();
  
  // Upload Processing Lock Check
  const checkUploadInProgress = (): number => {
    const lockTimestamp = localStorage.getItem('cv_upload_timestamp');
    if (lockTimestamp) {
      try {
        const timestamp = parseInt(lockTimestamp, 10);
        const elapsed = Date.now() - timestamp;
        const remaining = 6 * 60 * 1000 - elapsed;
        
        if (remaining > 0) {
          return Math.ceil(remaining / 60000); // return minutes left
        } else {
          localStorage.removeItem('cv_upload_timestamp');
        }
      } catch (e) {
        localStorage.removeItem('cv_upload_timestamp');
      }
    }
    return 0;
  };
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    career_status: 'invisible',
    subscription_type: 'explorer_free',
    is_recruiter: false,
    is_contactable: false,
    real_talk_sentence: '',
    real_talk_tags: [] as string[],
    base_locations: '',
    preferred_locations: '',
    role_preferences: '',
    current_role: '',
    current_level: ''
  });

  // Career status, job preferences and Real Talk now live on the Passport tab
  // (Status & Präferenzen page). Kept here behind a flag so the editing logic is
  // preserved/reversible while Profile is slimmed to CV + ATS + Account/Billing.
  const SHOW_LEGACY_PREFS = false;
  // CV display + ATS now live on the Passport CV page. Kept behind a flag so the
  // load/delete/readability logic stays referenced (and reversible).
  const SHOW_LEGACY_CV = false;

  const careerStatusOptions = [
    { value: 'actively_seeking', label: t('careerStatus.options.actively_seeking') },
    { value: 'curious', label: t('careerStatus.options.curious') },
    { value: 'invisible', label: t('careerStatus.options.invisible') }
  ];

  const subscriptionTypeOptions = [
    { value: 'explorer_free', label: 'Explorer Free', description: '30 Passport searches/month' },
    { value: 'explorer_premium', label: 'Explorer Premium', description: '300 Passport searches/month + more' },
    { value: 'recruiter_free', label: 'Recruiter Free', description: '30 Candidate searches/month' },
    { value: 'recruiter_premium', label: 'Recruiter Premium', description: '300 Candidate searches/month + more' }
  ];

  // Real Talk tag keys (stored in DB as English keys, displayed with translations)
  const realTalkTagKeys = [
    'took_career_break',
    'worked_unfulfilling_jobs',
    'doubted_career_path',
    'experienced_loss_passion',
    'still_finding_direction',
    'took_nonlinear_path',
    'rejected_more_than_accepted',
    'felt_imposter_syndrome',
    'chose_balance_over_pay',
    'took_paycut_for_purpose',
    'prioritized_mental_health',
    'experienced_layoff',
    'landed_job_by_luck',
    'was_underpaid_once',
    'quit_without_plan'
  ];

  // Helper function to get subscription display info
  const getSubscriptionInfo = (subscriptionType: string) => {
    const option = subscriptionTypeOptions.find(opt => opt.value === subscriptionType);
    return option || { value: subscriptionType, label: subscriptionType, description: 'Unknown' };
  };

  useEffect(() => {
    const loadProfileWhenReady = async () => {
      if (!isSignedIn || !user) {
        setLoading(true);
        return;
      }

      // Avoid duplicate attempts
      if (profile || hasAttemptedLoad) {
        return;
      }

      setHasAttemptedLoad(true);
      loadProfile();
    };

    loadProfileWhenReady();
  }, [isSignedIn, user?.userId]); // Same pattern as ProfileSync

  // After payment success: refetch profile so subscription_type updates (webhook may lag)
  useEffect(() => {
    if (!location.state?.fromPayment || !user?.userId) return;
    const refetch = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
        if (response.ok) {
          const data = await response.json();
          setProfile(data);
          setFormData(prev => ({ ...prev, subscription_type: data.subscription_type || 'explorer_free' }));
        }
      } catch { /* ignore */ }
    };
    const t1 = setTimeout(refetch, 2000);
    const t2 = setTimeout(refetch, 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.state?.fromPayment, user?.userId]);

  const loadProfile = async (retryCount = 0) => {
    try {
      setLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      // Add small delay to ensure auth token is properly set (same as ProfileSync)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user?.userId}`);
      
      if (response.ok) {
        const profileData = await response.json();
        console.debug('🔍 Profile.tsx - Profile loaded from backend:', profileData);
        console.debug('🔍 Profile.tsx - career_status:', profileData.career_status);
        console.debug('🔍 Profile.tsx - base_locations:', profileData.base_locations);
        console.debug('🔍 Profile.tsx - preferred_locations:', profileData.preferred_locations);
        console.debug('🔍 Profile.tsx - role_preferences:', profileData.role_preferences);
        console.debug('🔍 Profile.tsx - is_contactable:', profileData.is_contactable);

        // ❌ Redirect recruiters to recruiter profile page (silent redirect, no toast)
        if (profileData.is_recruiter) {
          navigate('/recruiter-profile', { state: location.state });
          return;
        }

        setProfile(profileData);
        setFormData({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          career_status: profileData.career_status || 'invisible',
          subscription_type: profileData.subscription_type || 'explorer_free',
          is_recruiter: profileData.is_recruiter || false,
          is_contactable: profileData.is_contactable ?? false,
          real_talk_sentence: profileData.real_talk_sentence || '',
          real_talk_tags: profileData.real_talk_tags || [],
          base_locations: profileData.base_locations || '',
          preferred_locations: profileData.preferred_locations || '',
          role_preferences: profileData.role_preferences || '',
          current_role: profileData.current_role || '',
          current_level: profileData.current_level || ''
        });
      } else if (response.status === 404) {
        // Profile doesn't exist yet - ProfileSync will create it
        setMessage({ type: 'error', text: 'Profile is being created. Please refresh the page in a moment.' });
      } else if (response.status === 403) {
        // Auth issue - just log it, same as ProfileSync
        console.debug('Auth issue detected, profile load failed');
        setMessage({ type: 'error', text: 'Authentication issue. Please refresh the page.' });
      } else {
        throw new Error(`Failed to load profile (${response.status})`);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const loadMockQuota = async () => {
    if (profile?.is_recruiter) return;
    try {
      setLoadingMockQuota(true);
      const { quota } = await getMockQuota(apiCall);
      setMockQuota(quota);
    } catch (e) {
      console.warn('Failed to load mock quota:', e);
    } finally {
      setLoadingMockQuota(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      setLoadingStats(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/auth/usage-stats`);
      
      if (response.ok) {
        const stats = await response.json();
        setUsageStats(stats);
      } else {
        console.error('Failed to load usage stats:', response.status);
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
    } finally {
      setLoadingStats(false);
    }
    await loadMockQuota();
  };

  const loadSubscription = async () => {
    try {
      setLoadingSubscription(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      if (!user?.userId) {
        console.warn('No user ID available for subscription check');
        return;
      }
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/subscription/${user.userId}`);
      
      if (response.ok) {
        const subscription = await response.json();
        console.debug('💳 Subscription loaded:', subscription);
        setSubscriptionInfo(subscription);
      } else {
        console.debug('No subscription found or error:', response.status);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const loadCvImages = async (cvId: string, attempt = 0) => {
    const MAX_RETRIES = 2;
    try {
      if (attempt === 0) setLoadingImages(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/cv/${cvId}/images`);
      
      if (response.ok) {
        const data = await response.json();
        const images = data.images || [];
        if (images.length > 0) {
          setCvImages(images);
        } else if (attempt < MAX_RETRIES) {
          // Images not ready yet (e.g. still processing after upload) - retry after delay
          console.debug(`CV images empty, retrying (${attempt + 1}/${MAX_RETRIES})...`);
          setLoadingImages(false);
          setTimeout(() => loadCvImages(cvId, attempt + 1), 2000 * (attempt + 1));
          return;
        } else {
          setCvImages([]);
        }
      } else if (attempt < MAX_RETRIES) {
        console.debug(`CV images fetch failed (${response.status}), retrying (${attempt + 1}/${MAX_RETRIES})...`);
        setLoadingImages(false);
        setTimeout(() => loadCvImages(cvId, attempt + 1), 2000 * (attempt + 1));
        return;
      } else {
        console.error('Failed to load CV images after retries:', response.status);
      }
    } catch (error) {
      if (attempt < MAX_RETRIES) {
        console.debug(`CV images error, retrying (${attempt + 1}/${MAX_RETRIES})...`);
        setLoadingImages(false);
        setTimeout(() => loadCvImages(cvId, attempt + 1), 2000 * (attempt + 1));
        return;
      }
      console.error('Error loading CV images after retries:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  const loadReadabilityScore = async (cvId: string) => {
    try {
      setLoadingReadability(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/readability/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cv_id: cvId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setJustAnalyzed(true); // Trigger animation
        setReadabilityScore({ score: data.score, summary: data.summary });
      } else {
        console.error('Failed to load readability score:', response.status);
        showToast(t('ats.analyzeError'), 'error');
      }
    } catch (error) {
      console.error('Error loading readability score:', error);
      showToast(t('ats.analyzeError'), 'error');
    } finally {
      setLoadingReadability(false);
    }
  };

  // Load CV images when profile is loaded and has cv_id
  useEffect(() => {
    if (profile?.cv_id && cvImages.length === 0 && !loadingImages) {
      loadCvImages(profile.cv_id);
    }
  }, [profile?.cv_id]);

  useEffect(() => {
    if (profile && !usageStats && !loadingStats) {
      loadUsageStats();
    }
  }, [profile]);

  useEffect(() => {
    if (usageExpanded && profile && !profile.is_recruiter) {
      loadMockQuota();
    }
  }, [usageExpanded, profile?.is_recruiter]);

  useEffect(() => {
    if (profile && !subscriptionInfo && !loadingSubscription) {
      loadSubscription();
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      const updateData = {
        ...formData
      };

      const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user?.userId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
        // Extended timeout for profile updates (includes Pinecone updates)
        signal: AbortSignal.timeout(60000) // 60 seconds
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        setEditingContact(false);
        setEditingCareer(false);
        setEditingJobPrefs(false);
        setLevelDropdownOpen(false);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } else if (response.status === 403) {
        setMessage({ type: 'error', text: 'Authentication issue. Please refresh the page and try again.' });
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to update profile: ${errorText}`);
      }
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      
      // Show user-friendly error messages
      if (error.message && error.message.includes('429')) {
        showToast('Too many requests. Please wait 30 seconds and try again.', 'error', 6000);
        setMessage({ type: 'error', text: 'Too many requests. Please wait and try again.' });
      } else if (error.message && error.message.includes('503')) {
        showToast('Service temporarily unavailable. Try again in a moment.', 'error');
        setMessage({ type: 'error', text: 'Service temporarily unavailable. Try again later.' });
      } else if (error.message && error.message.includes('504')) {
        showToast('Request timed out. Please try again.', 'error');
        setMessage({ type: 'error', text: 'Request timed out. Please try again.' });
      } else {
        showToast('Failed to update profile. Please try again.', 'error');
        setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        career_status: profile.career_status || 'invisible',
        subscription_type: profile.subscription_type || 'explorer_free',
        is_recruiter: profile.is_recruiter || false,
        is_contactable: profile.is_contactable ?? false,
        real_talk_sentence: profile.real_talk_sentence || '',
        real_talk_tags: profile.real_talk_tags || [],
        base_locations: profile.base_locations || '',
        preferred_locations: profile.preferred_locations || '',
        role_preferences: profile.role_preferences || '',
        current_role: profile.current_role || '',
        current_level: profile.current_level || ''
      });
    }
    setEditingContact(false);
    setEditingCareer(false);
    setEditingJobPrefs(false);
    setLevelDropdownOpen(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showToast('Please type DELETE to confirm', 'error');
      return;
    }

    try {
      setIsDeleting(true);
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/delete-account`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showToast('Account deleted successfully', 'success');
        // Sign out and redirect to home
        await signOut();
        navigate('/');
      } else {
        const errorData = await response.json();
        showToast(errorData.detail || 'Failed to delete account', 'error');
      }
    } catch (error: any) {
      console.error('Error deleting account:', error);
      showToast(error.message || 'Failed to delete account', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  };

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <User className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400 mx-auto mb-3" />
          <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-1">{t('signInRequired.title')}</h2>
          <p className="text-sm text-slate-600 mb-5">{t('signInRequired.message')}</p>
          <button
            onClick={() => navigate('/login?return=/profile')}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold rounded-full shadow-sm transition-all"
          >
            {t('signInRequired.signInButton')} →
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-pink-50/60 to-rose-50/60 px-4 sm:px-6 py-4 border-b border-slate-200 relative overflow-hidden">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-200 rounded-full animate-pulse flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-5 sm:h-7 w-40 sm:w-56 bg-slate-200 rounded animate-pulse" />
                  <div className="h-3.5 sm:h-4 w-48 sm:w-64 bg-slate-200 rounded animate-pulse" />
                  <div className="flex gap-2">
                    <div className="h-5 sm:h-6 w-16 sm:w-20 rounded-full bg-yellow-200 animate-pulse" />
                    <div className="h-5 sm:h-6 w-20 sm:w-24 rounded-full bg-slate-200 animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 sm:p-5 space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
                  <div className="h-4 sm:h-5 w-28 sm:w-36 bg-slate-200 rounded mb-3 animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-9 sm:h-10 w-full bg-slate-100 rounded-lg animate-pulse" />
                    <div className="h-9 sm:h-10 w-full bg-slate-100 rounded-lg animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Message */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4`}
          >
            <div className={`p-3 sm:p-4 rounded-lg flex items-center space-x-2 text-sm sm:text-base ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Saving Modal */}
      <AnimatePresence>
        {saving && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10001]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-[10001] px-4">
              <motion.div
                className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              >
                <div className="bg-gradient-to-r from-pink-500 to-rose-500 p-5 text-center">
                  <motion.div
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Sparkles className="w-10 h-10 text-white mx-auto mb-2 drop-shadow" />
                  </motion.div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    {t('updatingModal.title')}
                  </h3>
                </div>

                <div className="p-5 text-center">
                  <div className="flex items-center justify-center mb-3">
                    <Loader2 className="w-6 h-6 text-pink-500 animate-spin" />
                  </div>
                  <p className="text-sm text-slate-700 font-medium mb-1">
                    {t('updatingModal.warning')}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t('updatingModal.message')}
                  </p>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Deleting Modal */}
      <AnimatePresence>
        {deleting && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10001]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-[10001] px-4">
              <motion.div
                className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              >
                <div className="bg-gradient-to-r from-red-500 to-rose-500 p-5 text-center">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Trash2 className="w-10 h-10 text-white mx-auto mb-2 drop-shadow" />
                  </motion.div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    {t('deleteCV.title')}
                  </h3>
                </div>

                <div className="p-5 text-center">
                  <div className="flex justify-center mb-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-200 border-t-red-500"></div>
                  </div>
                  <p className="text-sm text-slate-600">
                    {t('deleteCV.message')}
                  </p>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-pink-50/60 to-rose-50/60 px-4 sm:px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                <User className="w-6 h-6 sm:w-7 sm:h-7 text-slate-500" />
              </div>
              <div className="text-slate-800 min-w-0">
                <h2 className="text-base sm:text-xl font-semibold text-slate-800 truncate">
                    {profile?.first_name && profile?.last_name
                      ? `${profile.first_name} ${profile.last_name}`
                      : profile?.first_name || t('header.welcome')}
                </h2>
                  <p className="text-slate-600 flex items-center mt-1 text-xs sm:text-sm truncate">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">{profile?.email}</span>
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center mt-2 gap-2 sm:gap-3">
                  {profile?.subscription_type && (
                    <span className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-xs font-medium border border-amber-200">
                      {getSubscriptionInfo(profile.subscription_type).label}
                    </span>
                  )}
                  {profile?.is_recruiter && (
                    <span className="bg-orange-100 text-orange-800 px-2.5 py-1 rounded-full text-xs font-medium border border-orange-200">
                      {i18n.language?.startsWith('de') ? 'Recruiter:in' : 'Recruiter'}
                    </span>
                  )}
                  
                  {/* Usage Stats Dropdown */}
                  <div className="relative" ref={usageDropdownRef}>
                    <button
                      onClick={() => setUsageExpanded(!usageExpanded)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-600 hover:bg-pink-50 hover:border-pink-200 transition-colors"
                    >
                      <BarChart3 className="w-3.5 h-3.5 text-pink-500" />
                      <span>{t('usageStats.title')}</span>
                      <motion.div
                        animate={{ rotate: usageExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                      </motion.div>
                    </button>
                    
                    {/* Dropdown Content */}
                    <AnimatePresence>
                      {usageExpanded && (
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.12 }}
                          className="absolute top-full left-0 mt-1.5 w-64 max-w-[calc(100vw-3rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-50 p-3"
                        >
                          {loadingStats ? (
                            <div className="flex items-center justify-center py-3">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-pink-200 border-t-pink-500"></div>
                            </div>
                          ) : usageStats ? (
                            <div className="space-y-2.5">
                              {(() => {
                                const visibleKeys = explorerUsageFeatureKeys();
                                const visible = visibleKeys
                                  .map((feature) => [feature, usageStats.features[feature as keyof UsageStats['features']]] as const)
                                  .filter((entry): entry is readonly [string, FeatureUsage] => !!entry[1]);
                                const visibleUsed = visible.reduce((sum, [, stats]) => sum + stats.used, 0);
                                const visibleLimit = visible.reduce((sum, [, stats]) => sum + stats.limit, 0);
                                const featureNames: Record<string, string> = {
                                  cv_search: 'Passport Search',
                                  creator_search: 'Creator Search',
                                  reach_outs: 'Reach Outs',
                                  cv_upload: 'CV Upload & ATS',
                                  job_watch: 'Job-Radar',
                                };
                                return (
                                  <>
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-[11px] font-medium text-slate-600">{t('usageStats.totalMonthly')}</span>
                                  <span className="text-[11px] text-slate-500">{visibleUsed}/{visibleLimit}</span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="bg-gradient-to-r from-pink-500 to-rose-400 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${visibleLimit ? Math.min((visibleUsed / visibleLimit) * 100, 100) : 0}%` }}
                                  ></div>
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1">
                                  {t('usageStats.resetsText', { date: new Date(usageStats.resets_at).toLocaleDateString(i18n.language) })}
                                </p>
                              </div>

                              <div className="space-y-1.5 pt-2 border-t border-slate-100">
                                {!profile?.is_recruiter && (
                                  <div className="flex justify-between items-center text-[11px]">
                                    <span className="text-slate-500">{t('usageStats.mockInterviews')}</span>
                                    {loadingMockQuota && !mockQuota ? (
                                      <span className="text-slate-300">…</span>
                                    ) : mockQuota ? (
                                      <span className={`font-medium ${!mockQuota.is_admin && mockQuota.remaining === 0 ? 'text-red-500' : 'text-slate-600'}`}>
                                        {mockQuota.is_admin
                                          ? t('usageStats.unlimited')
                                          : mockQuota.total <= 0
                                            ? t('usageStats.premiumOnly')
                                            : `${mockQuota.used}/${mockQuota.total}`}
                                      </span>
                                    ) : (
                                      <span className="text-slate-300"> - </span>
                                    )}
                                  </div>
                                )}
                                {visible.map(([feature, stats]) => {
                                    const featureName = featureNames[feature] || feature;
                                    const isDisabled = stats.limit === 0;
                                    return (
                                      <div key={feature} className="flex justify-between items-center text-[11px]">
                                        <span className="text-slate-500">{featureName}</span>
                                        <span className={`font-medium ${isDisabled ? 'text-slate-300' : stats.remaining === 0 ? 'text-red-500' : 'text-slate-600'}`}>
                                          {isDisabled ? '-' : `${stats.used}/${stats.limit}`}
                                        </span>
                                      </div>
                                    );
                                  })}
                              </div>
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400 text-center py-2">Unable to load</p>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  {/* Upgrade Button (only for free users) */}
                  {(profile?.subscription_type === 'explorer_free' || profile?.subscription_type === 'recruiter_free') && (
                    <button
                      onClick={() => navigate('/pricing')}
                      className="flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full text-xs font-medium hover:opacity-90 transition-opacity"
                    >
                      <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      <span>Upgrade</span>
                    </button>
                  )}
                </div>
              </div>
              </div>

              {/* OLD Edit Button REMOVED - now using granular edit buttons per section */}
            </div>
          </div>

          {/* Profile Content */}
          <div className="p-3 sm:p-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
              {/* Column 1 - Cevee Passport preview (CV + ATS moved to Passport) */}
              <div className="space-y-4 sm:space-y-6">
                <PassportPreviewCard
                  cvId={profile?.cv_id || null}
                  onOpen={() => navigate('/?tab=passport')}
                />
                {SHOW_LEGACY_CV && profile?.cv_id && (
                  <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-3">{t('cv.title')}</h3>
                    
                    {/* ATS Readability Score - only show if CV images loaded */}
                    {cvImages.length > 0 && !readabilityScore ? (
                      <button
                        onClick={() => profile.cv_id && loadReadabilityScore(profile.cv_id)}
                        disabled={loadingReadability}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-3 text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-sm disabled:opacity-70 disabled:cursor-wait"
                      >
                        {loadingReadability ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Analyzing...</span>
                          </>
                        ) : (
                          <>
                            <BarChart3 className="w-4 h-4" />
                            <span>Analyze ATS Readability</span>
                          </>
                        )}
                      </button>
                    ) : cvImages.length > 0 && readabilityScore ? (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <BarChart3 className="w-4 h-4 text-pink-600" />
                          <span className="text-xs font-semibold text-slate-700">ATS Readability Score</span>
                        </div>
                        
                        {/* Score Bar */}
                        <div className="relative h-4 bg-slate-200 rounded-full overflow-hidden mb-1.5">
                          {/* Background zones */}
                          <div className="absolute inset-0 flex">
                            <div className="w-[60%] bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400" />
                            <div className="w-[40%] bg-gradient-to-r from-yellow-400 via-lime-400 to-green-400" />
                          </div>
                          {/* Score indicator */}
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${readabilityScore.score}%` }}
                            transition={{ duration: 3, ease: "easeOut" }}
                            className="absolute inset-y-0 left-0 bg-black/20"
                          />
                          {/* Score marker */}
                          <motion.div
                            initial={{ left: '0%' }}
                            animate={{ left: `${readabilityScore.score}%` }}
                            transition={{ duration: 3, ease: "easeOut" }}
                            className="absolute top-0 bottom-0 w-1 bg-white shadow-lg transform -translate-x-1/2"
                          />
                        </div>
                        
                        {/* Score number */}
                        <div className="flex justify-center mb-2">
                          <span
                            className={`text-lg sm:text-xl font-semibold transition-colors duration-100 ${
                              displayScore >= 80 ? 'text-green-600' :
                              displayScore >= 60 ? 'text-yellow-600' :
                              displayScore >= 40 ? 'text-orange-500' :
                              'text-red-600'
                            }`}
                          >
                            {displayScore}
                          </span>
                        </div>
                        
                        {/* Summary */}
                        <div className="bg-white rounded-lg p-2.5 border border-slate-200">
                          <p className="text-xs text-slate-600 text-left leading-relaxed">
                            💡 {readabilityScore.summary}
                          </p>
                        </div>
                      </motion.div>
                    ) : null}
                    
                    {loadingImages ? (
                      <div className="p-4 sm:p-8">
                        <div className="aspect-[210/297] max-h-[40vh] mx-auto bg-slate-100 rounded-lg border border-slate-200 animate-pulse relative overflow-hidden">
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent z-10" />
                          <div className="p-6 sm:p-10 space-y-4">
                            <div className="h-5 w-2/5 bg-slate-200 rounded" />
                            <div className="space-y-2.5">
                              <div className="h-3 w-full bg-slate-200/70 rounded" />
                              <div className="h-3 w-4/5 bg-slate-200/70 rounded" />
                              <div className="h-3 w-3/5 bg-slate-200/70 rounded" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : cvImages.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4">
                        {cvImages.map((image, index) => (
                          <div key={index} className="relative">
                            <img 
                              src={`data:image/png;base64,${image}`}
                              alt={`CV Page ${index + 1}`}
                              className="w-full border rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                              onClick={() => {
                                const htmlContent = `
                                    <html>
                                      <head><title>CV Page ${index + 1}</title></head>
                                      <body style="margin:0;padding:20px;background:#f5f5f5;display:flex;justify-content:center;">
                                        <img src="data:image/png;base64,${image}" style="max-width:100%;height:auto;box-shadow:0 4px 6px rgba(0,0,0,0.1);" />
                                      </body>
                                    </html>
                                `;
                                const blob = new Blob([htmlContent], { type: 'text/html' });
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                              }}
                            />
                            <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                              Page {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center text-sm">
                        <p className="font-medium text-slate-700">{t('cv.noImagesFound')}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {t('cv.noImagesHint')}
                        </p>
                        <p className="text-xs text-slate-600 mt-3">
                          {t('cv.notUploadedYet')}
                        </p>
                        <button
                          onClick={() => navigate('/?tab=passport')}
                          className="mt-3 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white font-medium rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-sm"
                        >
                          {t('cv.uploadNow')}
                        </button>
                      </div>
                    )}
                    
                    {/* CV Actions - Upload & Delete */}
                    <div className="flex justify-center gap-3 mt-4 pt-4 border-t border-slate-100">
                      <button
                        onClick={() => navigate('/?tab=passport')}
                        className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title={t('cv.upload')}
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm(t('cv.deleteWarning'))) {
                            setDeleting(true);
                            try {
                              const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
                              
                              const response = await apiCall(`${apiBaseUrl}/api/v1/cvs/${profile.cv_id}`, {
                                method: 'DELETE',
                              });
                              
                              if (response.ok) {
                                setTimeout(() => {
                                  window.location.reload();
                                }, 1500);
                              } else {
                                setDeleting(false);
                                const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
                                setMessage({ type: 'error', text: `Failed to delete CV: ${error.detail || 'Unknown error'}` });
                              }
                            } catch (err) {
                              setDeleting(false);
                              console.error('Delete error:', err);
                              setMessage({ type: 'error', text: 'Failed to delete CV. Please try again.' });
                            }
                          }
                        }}
                        disabled={deleting}
                        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('cv.delete')}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
                
                {/* No CV uploaded at all */}
                {SHOW_LEGACY_CV && !profile?.cv_id && (
                  <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-3">{t('cv.title')}</h3>
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                      <p className="text-sm text-slate-700 font-medium">{t('cv.noCvUploaded')}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        {t('cv.uploadPrompt')}
                      </p>
                      <button
                        onClick={() => navigate('/?tab=passport')}
                        className="mt-4 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white font-medium rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-sm"
                      >
                        {t('cv.uploadNow')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Legacy Inspirer Contact (moved to Passport → Status & Präferenzen) */}
              {SHOW_LEGACY_PREFS && !editingContact && (
              <div className="space-y-4 sm:space-y-6">
                {/* Inspire & Contact Preferences */}
                <div id="inspirer-preferences" className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-800">
                      {t('contactInfo.sectionTitle')}
                    </h3>
                    <button
                      onClick={() => setEditingContact(true)}
                      className="flex items-center gap-1 text-sm px-3 py-1.5 text-pink-600 hover:text-pink-700 hover:bg-pink-100 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      {t('contactInfo.edit')}
                    </button>
                  </div>
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="text-xs text-slate-500 flex items-center gap-1.5">
                        <MessageCircle className="w-3.5 h-3.5 text-pink-600" />
                        {t('contactInfo.allowContact')}
                      </label>
                      <p className="text-sm text-slate-700">
                        {profile?.is_contactable ? t('contactInfo.yes') : `❌ ${t('contactInfo.no')}`}
                      </p>
                    </div>

                    {/* Real Talk (View Mode) */}
                    <div className="pt-3 sm:pt-4 border-t border-pink-200">
                      <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-2">
                        <Heart className="w-3.5 h-3.5 text-pink-600" />
                        {t('realTalk.title')}
                      </label>
                      <div className="text-xs text-slate-500 mb-2 sm:mb-3">
                        {t('realTalk.privacyNotice')}
                      </div>
                      
                      {formData.real_talk_sentence || (formData.real_talk_tags && formData.real_talk_tags.length > 0) ? (
                        <div className="space-y-2">
                          {formData.real_talk_sentence && (
                            <p className="text-sm text-slate-700 italic">
                              "{formData.real_talk_sentence}"
                            </p>
                          )}
                          {formData.real_talk_tags && formData.real_talk_tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {formData.real_talk_tags.map((tagKey) => (
                                <span
                                  key={tagKey}
                                  className="px-2 py-1 bg-pink-100 text-pink-700 text-xs font-medium rounded-full border border-pink-200"
                                >
                                  {t(`realTalk.tags.${tagKey}`)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2.5 p-3 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                          <p className="text-sm text-slate-600 font-medium">
                            {t('realTalk.notSet')}
                          </p>
                          <div className="space-y-2">
                            <p className="text-xs text-slate-500 italic opacity-75">
                              {t('realTalk.exampleSentence')}
                            </p>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">{t('realTalk.exampleTags')}</p>
                              <div className="flex flex-wrap gap-1.5">
                                <span className="px-2 py-1 bg-white text-slate-500 text-xs rounded-full border border-dashed border-slate-300">
                                  {t('realTalk.tags.still_finding_direction')}
                                </span>
                                <span className="px-2 py-1 bg-white text-slate-500 text-xs rounded-full border border-dashed border-slate-300">
                                  {t('realTalk.tags.felt_imposter_syndrome')}
                                </span>
                                <span className="px-2 py-1 bg-white text-slate-500 text-xs rounded-full border border-dashed border-slate-300">
                                  {t('realTalk.tags.rejected_more_than_accepted')}
                                </span>
                                <span className="px-2 py-1 bg-white text-slate-500 text-xs rounded-full border border-dashed border-slate-300">
                                  {t('realTalk.tags.chose_balance_over_pay')}
                                </span>
                                <span className="px-2 py-1 bg-white text-slate-500 text-xs rounded-full border border-dashed border-slate-300">
                                  {t('realTalk.tags.quit_without_plan')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* EDIT MODE - Contact Preferences (legacy; now on Passport) */}
              {SHOW_LEGACY_PREFS && editingContact && (
              <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
                <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-3 sm:mb-4 flex items-center">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-pink-600" />
                  {t('contactInfo.title')}
                </h3>
                <div className="bg-pink-50/50 p-3 rounded-lg border border-pink-200 mb-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="contactable-edit"
                      checked={formData.is_contactable}
                      onChange={(e) => setFormData({ ...formData, is_contactable: e.target.checked })}
                      className="w-4 h-4 mt-0.5 text-pink-600 border-slate-300 rounded focus:ring-pink-500"
                    />
                    <div className="flex-1">
                      <label htmlFor="contactable-edit" className="text-sm font-medium text-slate-800 cursor-pointer">
                        {t('contactInfo.allowContact')}
                      </label>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {t('contactInfo.allowContactHint')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Real Talk (Edit Mode) */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4 text-pink-600" />
                    <h4 className="text-sm font-semibold text-slate-800">{t('realTalk.title')}</h4>
                  </div>
                  
                  {/* Privacy Notice */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3">
                    <p className="text-xs text-slate-700">
                      {t('realTalk.privacyNotice')}
                    </p>
                  </div>

                  {/* Sentence Input */}
                  <div className="mb-3">
                    <label className="block text-xs text-slate-500 mb-1.5">
                      {t('realTalk.sentenceLabel')}
                    </label>
              <textarea
                value={formData.real_talk_sentence}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 500) {
                    setFormData({ ...formData, real_talk_sentence: value });
                  }
                }}
                placeholder={t('realTalk.sentencePlaceholder')}
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-none"
              />
                  </div>

                  {/* Tags */}
                  <div className="mb-3">
                    <label className="block text-xs text-slate-500 mb-2">
                      {t('realTalk.tagsLabel')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {realTalkTagKeys.map((tagKey) => {
                        const isSelected = formData.real_talk_tags.includes(tagKey);
                        const canSelect = formData.real_talk_tags.length < 20 || isSelected;
                        
                        return (
                          <button
                            key={tagKey}
                            onClick={() => {
                              if (isSelected) {
                                // Remove tag
                                setFormData({
                                  ...formData,
                                  real_talk_tags: formData.real_talk_tags.filter(t => t !== tagKey)
                                });
                              } else if (canSelect) {
                                // Add tag
                                setFormData({
                                  ...formData,
                                  real_talk_tags: [...formData.real_talk_tags, tagKey]
                                });
                              }
                            }}
                            disabled={!canSelect && !isSelected}
                            className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                              isSelected
                                ? 'bg-pink-500 text-white shadow-sm'
                                : canSelect
                                ? 'bg-white text-slate-700 border border-slate-300 hover:border-pink-400 hover:bg-pink-50'
                                : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                            }`}
                          >
                            {t(`realTalk.tags.${tagKey}`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{saving ? t('contactInfo.saving') : t('contactInfo.save')}</span>
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>{t('contactInfo.cancel')}</span>
                  </button>
                </div>
              </div>
              )}

              {/* Column 3 - Career Status, Job Preferences, Usage & Limits */}
              <div className="space-y-4 sm:space-y-6">
              {SHOW_LEGACY_PREFS && !editingContact && (
              <>
                {/* View Mode - Career Status */}
                {!editingCareer && (
                <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-800">
                      {t('careerStatus.title')}
                    </h3>
                    <button
                      onClick={() => {
                        const minutesLeft = checkUploadInProgress();
                        if (minutesLeft > 0) {
                          showToast(t('cv.uploadWait', { minutes: minutesLeft }), 'info');
                          return;
                        }
                        setEditingCareer(true);
                      }}
                      className="flex items-center gap-1 text-sm px-3 py-1.5 text-pink-600 hover:text-pink-700 hover:bg-pink-100 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      {t('careerStatus.edit')}
                    </button>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5 text-pink-600" />
                      {t('careerStatus.visibilityLabel')}
                    </label>
                    <p className="text-sm text-slate-700 mt-1">
                      {careerStatusOptions.find(opt => opt.value === profile?.career_status)?.label || t('careerStatus.notSpecified')}
                    </p>
                  </div>
                </div>
                )}

                {/* Edit Mode - Career Status */}
                {editingCareer && (
                <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-3 flex items-center">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-pink-600" />
                    {t('careerStatus.title')}
                  </h3>
                  <div className="space-y-2 mb-4">
                    {careerStatusOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                          formData.career_status === option.value
                            ? 'border-pink-500 bg-pink-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="career_status"
                          value={option.value}
                          checked={formData.career_status === option.value}
                          onChange={(e) => setFormData({ ...formData, career_status: e.target.value })}
                          className="sr-only"
                        />
                        <div className="flex-1">
                          <div className="text-sm text-slate-700">{option.label}</div>
                        </div>
                        {formData.career_status === option.value && (
                          <Check className="w-4 h-4 text-pink-500 flex-shrink-0" />
                        )}
                      </label>
                    ))}
                  </div>
                  {formData.career_status && formData.career_status !== 'invisible' && (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 mb-4">
                      <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 leading-relaxed">{t('careerStatus.atsHint')}</p>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      <span>{saving ? t('careerStatus.saving') : t('careerStatus.save')}</span>
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>{t('careerStatus.cancel')}</span>
                    </button>
                  </div>
                </div>
                )}

                {/* Job Preferences */}
                <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-800">
                      {t('jobPreferences.title')}
                    </h3>
                    {!editingJobPrefs && profile?.cv_id && (
                      <button
                        onClick={() => {
                          const minutesLeft = checkUploadInProgress();
                          if (minutesLeft > 0) {
                            showToast(t('cv.uploadWait', { minutes: minutesLeft }), 'info');
                            return;
                          }
                          setEditingJobPrefs(true);
                          const dbLevels = (profile?.current_level || '').split(',').map(l => l.trim()).filter(Boolean);
                          setSelectedLevels(mapLevelValues(dbLevels, currentLanguage));
                        }}
                        className="flex items-center gap-1 text-sm px-3 py-1.5 text-pink-600 hover:text-pink-700 hover:bg-pink-100 rounded-lg transition-colors"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        {t('contactInfo.edit')}
                      </button>
                    )}
                  </div>

                  {!editingJobPrefs ? (
                    <>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.baseLocations')}
                          </label>
                          <p className="text-sm text-slate-700">
                            {profile?.base_locations || t('jobPreferences.notSpecified')}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Globe className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.preferredLocations')}
                          </label>
                          <p className="text-sm text-slate-700">
                            {profile?.preferred_locations || t('jobPreferences.notSpecified')}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Heart className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.rolePreferences')}
                          </label>
                          <p className="text-sm text-slate-700">
                            {profile?.role_preferences || t('jobPreferences.notSpecified')}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Star className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.currentRole')}
                          </label>
                          <p className="text-sm text-slate-700">
                            {profile?.current_role || t('jobPreferences.notSpecified')}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Target className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.currentLevel')}
                          </label>
                          <p className="text-sm text-slate-700">
                            {profile?.current_level || t('jobPreferences.notSpecified')}
                          </p>
                        </div>
                      </div>
                      {!profile?.cv_id && (
                        <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-200">
                          {t('jobPreferences.uploadFirst')}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                            <MapPin className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.baseLocations')}
                          </label>
                          <input
                            type="text"
                            value={formData.base_locations}
                            onChange={(e) => setFormData({ ...formData, base_locations: e.target.value })}
                            placeholder={t('jobPreferences.baseLocationsPlaceholder')}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                            <Globe className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.preferredLocations')}
                          </label>
                          <input
                            type="text"
                            value={formData.preferred_locations}
                            onChange={(e) => setFormData({ ...formData, preferred_locations: e.target.value })}
                            placeholder={t('jobPreferences.preferredLocationsPlaceholder')}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                            <Heart className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.rolePreferences')}
                          </label>
                          <input
                            type="text"
                            value={formData.role_preferences}
                            onChange={(e) => setFormData({ ...formData, role_preferences: e.target.value })}
                            placeholder={t('jobPreferences.rolePreferencesPlaceholder')}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                            <Star className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.currentRole')}
                          </label>
                          <input
                            type="text"
                            value={formData.current_role}
                            onChange={(e) => setFormData({ ...formData, current_role: e.target.value })}
                            placeholder={t('jobPreferences.currentRolePlaceholder')}
                            maxLength={200}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                            <Target className="w-3.5 h-3.5 text-pink-600" />
                            {t('jobPreferences.currentLevel')}
                          </label>
                          <div className="relative" ref={levelDropdownRef}>
                            <button
                              type="button"
                              onClick={() => setLevelDropdownOpen(!levelDropdownOpen)}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-white text-left pr-10"
                            >
                              <span className={selectedLevels.length === 0 ? 'text-slate-400' : 'text-slate-700'}>
                                {selectedLevels.length === 0
                                  ? t('jobPreferences.currentLevelPlaceholder')
                                  : selectedLevels.length === 1
                                  ? selectedLevels[0]
                                  : t('jobPreferences.levelsSelected', { count: selectedLevels.length })}
                              </span>
                              {levelDropdownOpen
                                ? <ChevronUp className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              }
                            </button>
                            {levelDropdownOpen && (
                              <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {getLevelOptions().map((option) => (
                                  <label key={option.value} className="flex items-center px-3 py-2 hover:bg-pink-50 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={selectedLevels.includes(option.value)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedLevels([...selectedLevels, option.value]);
                                        } else {
                                          setSelectedLevels(selectedLevels.filter(l => l !== option.value));
                                        }
                                      }}
                                      className="mr-2 h-4 w-4 text-pink-600 focus:ring-pink-400 border-slate-300 rounded"
                                    />
                                    <span className="text-sm text-slate-700">{option.label}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-4 pt-4 border-t border-slate-200">
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          {t('careerStatus.save')}
                        </button>
                        <button
                          onClick={handleCancel}
                          className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          {t('careerStatus.cancel')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                  </>
                )}

                {/* Premium Plans section moved to header as Upgrade button */}

                {/* Subscription Management (when payments enabled) */}
                {paymentsEnabled && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-sm sm:text-base font-semibold text-slate-800">
                        {t('billing.title')}
                      </h3>
                    </div>
                    
                    {profile ? (
                      <div className="space-y-3">
                        <div className="p-3 bg-white rounded-lg border border-slate-200">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-slate-600">{t('billing.currentPlan')}</span>
                            <span className="text-xs font-semibold text-pink-600">
                              {profile.subscription_type === 'explorer_premium' ? t('subscription.explorer_premium') : 
                               profile.subscription_type === 'explorer_free' ? t('subscription.explorer_free') :
                               profile.subscription_type === 'recruiter_premium' ? t('subscription.recruiter_premium') :
                               profile.subscription_type === 'recruiter_free' ? t('subscription.recruiter_free') : 
                               t('subscription.explorer_free')}
                            </span>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="space-y-1.5 sm:space-y-2">
                          {(profile.subscription_type === 'explorer_free' || profile.subscription_type === 'recruiter_free') ? (
                            <button
                              onClick={() => navigate('/pricing')}
                              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold py-2 sm:py-2.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-xs sm:text-sm"
                            >
                              <Sparkles className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                              {t('billing.upgradeToPremium')}
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate('/settings/billing')}
                              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold py-2 sm:py-2.5 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-xs sm:text-sm"
                            >
                              {t('billing.manageBilling')}
                            </button>
                          )}
                          <button
                            onClick={() => navigate('/pricing')}
                            className="w-full bg-white hover:bg-slate-50 text-slate-700 font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg border border-slate-300 transition-colors text-xs sm:text-sm"
                          >
                            {t('billing.viewAllPlans')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-2 sm:py-4">
                        <button
                          onClick={() => navigate('/pricing')}
                          className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold py-2 sm:py-2.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-xs sm:text-sm"
                        >
                          <Sparkles className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                          {t('billing.upgradeToPremium')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Danger Zone */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-xs font-medium text-slate-600">{t('deleteAccount.dangerZoneTitle')}</span>
                  </div>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-2.5 py-1 rounded-md transition-colors"
                  >
                    {t('deleteAccount.deleteButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4"
            onClick={() => !isDeleting && setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-800">
                  {t('deleteAccount.modalTitle')}
                </h3>
              </div>

              <div className="mb-5">
                <p className="text-sm text-slate-700 mb-3">
                  {t('deleteAccount.modalDescription')}
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 mb-3">
                  <li>{t('deleteAccount.item1')}</li>
                  <li>{t('deleteAccount.item2')}</li>
                  <li>{t('deleteAccount.item3')}</li>
                  <li>{t('deleteAccount.item4')}</li>
                  <li>{t('deleteAccount.item5')}</li>
                </ul>
                <p className="text-sm font-semibold text-red-600">
                  {t('deleteAccount.warningFinal')}
                </p>
              </div>

              <div className="mb-5">
                <label className="block text-xs text-slate-500 mb-2">
                  {t('deleteAccount.confirmLabel')} <span className="font-semibold text-slate-700">DELETE</span> {t('deleteAccount.toConfirm')}
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  disabled={isDeleting}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t('deleteAccount.deleting')}</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>{t('deleteAccount.confirmButton')}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {t('deleteAccount.cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profile;
